import { createAction, createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import auth0, { Auth0DecodedHash, Auth0Error } from 'auth0-js';

import { deleteAuthCookie, requestStatus } from '@clatter/platform';
import { config } from '../config';
import { deleteLocalStorageAuth, setLocalStorageAccessAuth } from '../helpers/auth';
import { NG_IMPERSONATE_FEATURE_KEY, stopImpersonating } from './impersonate.slice';
import { baseApi } from '../services/baseApi';
import { usersApi } from '../services/users.service';

interface AuthState {
  loadingStatus:
    | typeof requestStatus.initial
    | typeof requestStatus.error
    | typeof requestStatus.pending
    | typeof requestStatus.fulfilled;
  userAuth: UserAuth | null;
  error?: null | string;
}

const initialUserAuth = {
  user: null,
  accessToken: null,
  idToken: null,
};

export const AUTH_FEATURE_KEY = 'auth';

export const authAdapter = createEntityAdapter();

//region THUNKS
export const checkSession = createAsyncThunk<any, void, { rejectValue: unknown }>(
  'auth/checkSession',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      return await new Promise<Auth0DecodedHash>((resolve, reject) => {
        webAuth.checkSession(
          {
            audience: config.auth.audience,
            scope: config.auth.scope,
            responseType: config.auth.responseType,
            clientID: config.auth.clientID,
          },
          async (error: Auth0Error | null, authResult: Auth0DecodedHash | null) => {
            if (!error && authResult) {
              await dispatch(setSession({ authResult }));
              resolve(authResult);
            } else {
              // Handle the error within the callback
              reject(error);
            }
          },
        );
      });
    } catch (error) {
      console.log('Session check failed, performing cleanup...');
      deleteLocalStorageAuth();
      return rejectWithValue(error);
    }
  },
);

export const setSession = createAsyncThunk(
  'auth/setSession',
  async ({ authResult }: { authResult: Auth0DecodedHash }, { dispatch }) => {
    const { idTokenPayload, accessToken, idToken } = authResult;

    setLocalStorageAccessAuth(authResult);

    if (!accessToken) {
      console.error('No access token provided');
      return;
    }

    if (!idToken) {
      console.error('No id token provided');
      return;
    }

    if (!idTokenPayload) {
      console.error('No id token payload provided');
      return;
    }

    await dispatch(
      setUserAuthState({
        user: idTokenPayload,
        accessToken: accessToken,
        idToken: idToken,
      }),
    );
  },
);

//@ts-ignore
export const logoutUser = createAsyncThunk('auth/logout', async ({ returnTo } = {}, { dispatch }) => {
  // clear app state and stored token
  deleteLocalStorageAuth();
  deleteAuthCookie();

  await dispatch(stopImpersonating());

  await dispatch(setUserAuthState(initialUserAuth));

  // Reset RTK Query state to cancel all ongoing queries and clear cache
  dispatch(baseApi.util.resetApiState());
  dispatch(usersApi.util.resetApiState());

  if (!process.env?.['NX_PUBLIC_AUTH0_CLIENT_ID']) {
    throw new Error('Error logging out no "clientID" provided!');
  }

  // perform auth0 logout
  try {
    webAuth.logout({
      clientID: process.env?.['NX_PUBLIC_AUTH0_CLIENT_ID'],
      returnTo: returnTo || config.auth.loginUrl,
    });
  } catch (error) {
    console.error('Auth0 logout failed, forcing manual navigation:', error);
    window.location.href = returnTo || config.auth.loginUrl;
  }

  return Promise.resolve();
});
//endregion

//region ACTIONS
export const setUserAuthState = createAction<UserAuth>('SET_USER_AUTH_STATE');
//endregion

export const initialAuthState = authAdapter.getInitialState<AuthState>({
  loadingStatus: requestStatus.initial,
  userAuth: initialUserAuth,
  error: null,
});

//@ts-ignore
export const authSlice = createSlice({
  name: AUTH_FEATURE_KEY,
  initialState: initialAuthState,
  extraReducers: (builder) => {
    builder
      .addCase(setUserAuthState, (state, { payload }) => {
        state.userAuth = { user: payload.user, accessToken: payload.accessToken, idToken: payload.idToken };
      })
      .addCase(checkSession.pending, (state) => {
        state.loadingStatus = requestStatus.pending;
      })
      .addCase(checkSession.fulfilled, (state) => {
        state.loadingStatus = requestStatus.fulfilled;
      })
      .addCase(checkSession.rejected, (state) => {
        console.error('Check session error');
        state.loadingStatus = requestStatus.error;
        state.error = 'Check session error';
      })
      .addCase(logoutUser.pending, (state) => {
        state.loadingStatus = requestStatus.pending;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loadingStatus = requestStatus.fulfilled;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        console.error('Logout error', action.error);
        state.loadingStatus = requestStatus.error;
        state.error = action.error.message || 'Logout error';
      });
  },
});

export const authReducer = authSlice.reducer;

const {} = authAdapter.getSelectors();
export const getAuthState = (rootState: any) => rootState[AUTH_FEATURE_KEY];
export const getImpersonatedState = (rootState: any) => rootState[NG_IMPERSONATE_FEATURE_KEY];

export const authSelectors = {
  getUserAuthState: createSelector(getAuthState, getImpersonatedState, ({ userAuth }, { impersonated }): UserAuth => {
    if (!impersonated) {
      return userAuth;
    }

    const userAuthToImpersonated = {
      ...userAuth,
      user: {
        ...impersonated.idTokenPayload,
        [`${process.env.NX_PUBLIC_AUTH0_NAMESPACE}/roles`]: impersonated?.idTokenPayload?.roles || [],
      },
      activeUser: userAuth.user,
    };

    return userAuthToImpersonated;
  }),
  loadingStatus: createSelector(getAuthState, ({ loadingStatus }) => loadingStatus),
  isAuthenticated: createSelector(getAuthState, ({ userAuth }) => !!userAuth.accessToken),
  getImpersonatedUser: createSelector(getAuthState, ({ impersonated }) => impersonated?.user),
};

const webAuth = new auth0.WebAuth({
  domain: config.auth.domain!,
  clientID: config.auth.clientID!,
  audience: config.auth.audience,
  scope: 'openid profile email',
  responseType: 'token id_token',
  redirectUri: typeof window === 'object' ? window.location.origin : '/',
});
